import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Stack,
  CircularProgress,
  Chip,
  IconButton,
} from "@mui/material";
import MarkdownRenderer from "../../MarkdownRenderer";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";

type ActionButton = {
  label: string;
  message: string;
  variant?: "contained" | "outlined";
};

type UtilityAgentWithButtonsProps = {
  parsedContent: any;
  agentId: string;
  messageId?: string;
};

// Configuration for different agent types
const AGENT_CONFIGS: Record<
  string,
  {
    buttons: ActionButton[];
    endpoint: string;
    numericAgentId: number;
  }
> = {
  utility_agent_redline_intake: {
    buttons: [
      {
        label: "Our direction",
        message: "Our direction",
        variant: "contained",
      },
      {
        label: "Counterparty direction",
        message: "Counterparty direction",
        variant: "outlined",
      },
    ],
    endpoint: "/stream/redline",
    numericAgentId: 52,
  },
  utility_agent_redline_doc_analyzer: {
    buttons: [],
    endpoint: "/stream/redline",
    numericAgentId: 52,
  },
  utility_agent_redline_doc_analyzer_sow_buttons: {
    buttons: [
      {
        label: "Parent MSA",
        message: "Parent MSA",
        variant: "contained",
      },
      {
        label: "Standalone",
        message: "Standalone",
        variant: "outlined",
      },
    ],
    endpoint: "/stream/redline",
    numericAgentId: 52,
  },
  utility_agent_redline_doc_analyzer_sow_msa: {
    buttons: [],
    endpoint: "/stream/redline",
    numericAgentId: 52,
  },
};

export default function UtilityAgentWithButtons({
  parsedContent,
  agentId,
  messageId,
}: UtilityAgentWithButtonsProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const loading = useSelector((state: RootState) => state.user.loading);
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const [clickedButton, setClickedButton] = useState<string | null>(null);
  const [wasLoading, setWasLoading] = useState(false);

  // File upload state for sow_msa agent
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const config = AGENT_CONFIGS[agentId];
  const isSowMsaAgent =
    agentId === "utility_agent_redline_doc_analyzer_sow_msa";

  // Unified button styling
  const commonButtonStyle = {
    textTransform: "none" as const,
    borderColor: "#b2e5de",
    color: "#004141",
    backgroundColor: "#fff",
    fontWeight: 500,
    fontSize: "13px",
    px: 2.5,
    py: 0.75,
    "&:hover": {
      borderColor: "#00a888",
      backgroundColor: "#f0fbf9",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
      borderColor: "#ccc",
      backgroundColor: "#f5f5f5",
      color: "#999",
    },
  };

  // Check if this is the most recent agent message overall
  const isLatestMessage = React.useMemo(() => {
    if (!messageId) return true; // If no messageId, assume it's latest (shouldn't happen)

    // Find the last agent message (any agent type)
    const lastAgentMessage = [...currentChat]
      .reverse()
      .find((msg) => msg.sender === "agent");

    return lastAgentMessage?.id === messageId;
  }, [currentChat, messageId]);

  // Reset button state when loading completes (new response received)
  useEffect(() => {
    if (wasLoading && !loading) {
      // Loading just finished, reset the button state
      setClickedButton(null);
      setIsUploading(false);
      // Clear file after successful upload
      if (isSowMsaAgent) {
        setSelectedFile(null);
      }
    }
    setWasLoading(loading);
  }, [loading, wasLoading, isSowMsaAgent]);

  // Extract content safely - follow pattern from other agents
  const data = parsedContent?.finalAnswer || parsedContent;
  const content =
    typeof data === "string"
      ? data
      : data !== undefined && data !== null
        ? String(data)
        : "";

  if (!config) {
    console.warn(`No configuration found for agentId: ${agentId}`);
    return <Box>{content && <MarkdownRenderer content={content} />}</Box>;
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
    ];

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      alert("Only PDF, DOCX, and DOC files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert("File size must be less than 10 MB.");
      return;
    }

    setSelectedFile(file);

    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const handleConfirmUpload = () => {
    if (!selectedFile) {
      console.error("No file selected");
      return;
    }
    if (isUploading) return;

    setIsUploading(true);

    const params = new URLSearchParams(location.search);
    const sessionId = params.get("sessionId");

    // Ensure file is in an array for proper FormData handling
    const filesToUpload: File[] = [selectedFile];

    console.log("Sending file upload request:", {
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      fileType: selectedFile.type,
      filesArray: filesToUpload,
    });

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || config.numericAgentId,
        message: "File uploaded",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: config.endpoint,
        sessionId,
        files: filesToUpload,
      } as any), // Type assertion to ensure files is passed correctly
    );
  };

  const handleButtonClick = (buttonLabel: string, message: string) => {
    if (clickedButton) return; // Prevent multiple clicks

    setClickedButton(buttonLabel);

    const params = new URLSearchParams(location.search);
    const sessionId = params.get("sessionId");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || config.numericAgentId,
        message,
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: config.endpoint,
        sessionId,
      }),
    );
  };

  return (
    <Box>
      {content && <MarkdownRenderer content={content} />}

      {/* File upload section for sow_msa agent */}
      {isSowMsaAgent && isLatestMessage && (
        <Box mt={2}>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx"
            style={{ display: "none" }}
          />

          {/* Upload button or selected file display */}
          {!selectedFile ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || isUploading}
              sx={commonButtonStyle}
            >
              Upload Parent MSA Document
            </Button>
          ) : (
            <Stack direction="row" gap={5} alignItems="center">
              <Chip
                label={selectedFile.name}
                onDelete={handleRemoveFile}
                deleteIcon={<CloseIcon />}
                sx={{
                  maxWidth: "300px",
                  backgroundColor: "#e8f5f3",
                  color: "#004141",
                  borderColor: "#00d2aa",
                  border: "1px solid",
                  "& .MuiChip-deleteIcon": {
                    color: "#004141",
                    "&:hover": {
                      color: "#002020",
                    },
                  },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleConfirmUpload}
                disabled={loading || isUploading}
                sx={commonButtonStyle}
              >
                Confirm
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {config.buttons.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={5} mt={2}>
          {config.buttons.map((button) => {
            const isThisButtonClicked = clickedButton === button.label;
            // Disable if: not the latest message, or a button was clicked, or currently loading
            const isDisabled =
              !isLatestMessage || clickedButton !== null || loading;

            return (
              <Button
                key={button.label}
                size="small"
                variant="outlined"
                onClick={() => handleButtonClick(button.label, button.message)}
                disabled={isDisabled}
                sx={commonButtonStyle}
              >
                {button.label}
              </Button>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
